import type { Bundle, CarePlan, Condition, PlanDefinition } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import React from 'react';
import { Step } from 'semantic-ui-react';
import type { ReferralSummary } from 'types';
import type { FlatCondition } from 'types/flat-fhir';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import * as FHIRUtils from '../../../../utils/FHIRUtils';
import type { StepMutate, StepPatientIdentity, StepPractitionerData, StepTasks } from '../types';

import Assessment from './assess';
import CareTeamCard from './careteam';
import Intervention from './intervene';
import Launch from './launch';
import Screening from './screen';



/**
 * Props for {@link CarePlanSteps}, as passed by `careplan/index.tsx`.
 *
 * The three `*Data` props arrive as whole bundles and are unwrapped here to the
 * single resource each step needs (`entry[0].resource`); `tasks` by contrast
 * arrives already unwrapped as `tasksData.entry`.
 */
type CarePlanStepsProps = StepPatientIdentity & {
  /** `/CarePlan/?patient=...`; `entry[0].resource` is the member's plan. */
  carePlanData?: Bundle | undefined;
  /** `/PlanDefinition?identifier=...`; drives which steps exist. */
  planDefData?: Bundle | undefined;
  /** `/Condition/?patient=...&code=733423003`, the screening condition. */
  conditionData?: Bundle | undefined;
  /** The task bundle's `entry` array; sorted in place below. */
  tasks?: StepTasks;
  /** Re-runs every care-plan query in the parent. */
  refresh: StepMutate;
  /** Opens the survey sidebar for a questionnaire id. */
  toggleSurveySidebar?: ((questionnaireId: string) => void) | undefined;
  /**
   * Revalidates the email/SMS communication log.
   *
   * Required because `Launch` calls it unguarded after each SendGrid post.
   * `careplan/index.tsx` always forwards it from `patient/index.tsx`.
   */
  communicationsMutate: StepMutate;
  /** Revalidates the profile observation query. */
  profileMutate?: StepMutate | undefined;
  /** Active referrals for the member. */
  referralData?: unknown;
  /** The practitioner picker list, filtered upstream to GUID ids. */
  PractitionerData?: StepPractitionerData;
  /** The org's questionnaire bundle, for the web-survey actions. */
  questionnaireData?: Bundle | undefined;
  /** The member's `/CareTeam` bundle. */
  careTeamData?: Bundle | undefined;
  /** Revalidates the member's care team. */
  careTeamMutate?: StepMutate | undefined;
  /**
   * True while any of the parent's refresh queries is in flight.
   *
   * Required because `Launch` folds it into `loading || props.isRefreshing` and
   * feeds the result to semantic-ui `disabled` props, which reject `undefined`.
   * `careplan/index.tsx` always computes and passes it.
   */
  isRefreshing: boolean;
};

function CarePlanSteps(props: CarePlanStepsProps) {
  // Seeded `null` and written from `handleAssess` with the chosen action's id.
  const [plannedInterventionId, setPlannedInterventionId] = React.useState<string | null>(null);

  // These three are assigned only inside their `if`, so each is genuinely
  // `undefined` when its query returned no entry — which the render already
  // relies on (`carePlan == null`, `{carePlan && ...}`, `condition != null`).
  //
  // The `!` on `entry[0]` and `.resource` preserve existing throws: `entry: []`
  // and an entry with no `resource` both fault on the next property read today,
  // and a guard would silently skip the step instead. The resource type is
  // asserted because each is a single-type search.
  let carePlan: CarePlan | undefined;
  if (props.carePlanData && props.carePlanData.entry) {
    carePlan = props.carePlanData.entry[0]!.resource! as CarePlan;
  }

  let condition: Condition | undefined;
  let flatCondition: FlatCondition | undefined;
  if (props.conditionData && props.conditionData.entry) {
    condition = props.conditionData.entry[0]!.resource! as Condition;
    if (condition) {
      flatCondition = FHIRUtils.parseFHIRCondition(condition);
    }
  }

  let planDefinition: PlanDefinition | undefined;
  if (props.planDefData && props.planDefData.entry) {
    planDefinition = props.planDefData.entry[0]!.resource! as PlanDefinition;
  }

  //if assessment is done. Then get the planned intervention
  let intervention: string | undefined;
  if (carePlan && flatCondition && flatCondition.verificationStatusCode === 'confirmed') {
    //get intervention (for completed assessments)
    intervention = fhirpath
      .evaluate(
        carePlan,
        "identifier.where(system = 'https://projectwell.io/fhir/identifiers/selected-intervention').value"
      )
      .toString();
  }

  //intervention step is not shown for 'auto' interventions.
  let showIntervention = false;
  const interventionType = fhirpath
    .evaluate(planDefinition, "action.where(id='intervention').code.first().text")
    .toString();
  if (interventionType && interventionType !== 'auto') {
    showIntervention = true;
  }

  const handleLaunch = () => {
    props.refresh();
  };

  const handleCareTeamUpdate = () => {
    props.refresh();
  };

  const handleScreen = () => {
    props.refresh();
  };

  const handleAssess = (plannedInterventionAction: { id: string }) => {
    setPlannedInterventionId(plannedInterventionAction.id);
    props.refresh();
  };

  const handleIntervention = () => {
    props.refresh();
  };

  // SUSPECTED DEFECT — `Task.executionPeriod` is `0..1` in FHIR and is read here
  // without a guard, so a Task carrying no `executionPeriod` throws "Cannot read
  // properties of undefined (reading 'end')" during render and takes the whole
  // care-plan tab down. The inner `?? ` only covers an absent `end`, not an
  // absent `executionPeriod`. Note also that this sorts `props.tasks` IN PLACE
  // during render — it mutates the array the parent owns.
  //
  // Reported, not fixed: `?.` here would silently sort period-less tasks to the
  // epoch, reordering the stepper's task list rather than failing. The `!`
  // preserves the existing throw exactly.
  props.tasks?.sort((a: ResourceEntry<'Task'>, b: ResourceEntry<'Task'>) => {
    return (
      new Date(a.resource.executionPeriod!.end ?? a.resource.executionPeriod!.start!).getTime() -
      new Date(b.resource.executionPeriod!.end ?? b.resource.executionPeriod!.start!).getTime()
    );
  });

  return (
    <Step.Group size="tiny" ordered style={{ overflow: 'auto' }}>
      <Step active={carePlan == null} completed={carePlan != null}>
        <Step.Content>
          <Step.Title>Launch</Step.Title>
          <Step.Description>
            <Launch
              fhirPatient={props.fhirPatient}
              orgId={props.orgId}
              patientId={props.patientId}
              carePlan={carePlan}
              planDefinition={planDefinition}
              patientEmail={props.patientEmail}
              patientPhone={props.patientPhone}
              onLaunch={handleLaunch}
              patientName={props.patientName}
              communicationsMutate={props.communicationsMutate}
              isRefreshing={props.isRefreshing}
            />
          </Step.Description>
        </Step.Content>
      </Step>
      <Step disabled={carePlan == null} completed={carePlan != null}>
        <Step.Content>
          <Step.Title>Assign(ed) Care Team</Step.Title>
          <Step.Description>
            {carePlan && (
              // Asserted, not guarded: an absent value must still reach the child, as
              // it does today.
              <CareTeamCard
                fhirPatient={props.fhirPatient!}
                orgId={props.orgId!}
                patientId={props.patientId!}
                carePlan={carePlan}
                onUpdate={handleCareTeamUpdate}
                PractitionerData={props.PractitionerData}
                careTeamData={props?.careTeamData}
                careTeamMutate={props?.careTeamMutate as StepMutate}
              />
            )}
            {/* {!carePlan && <h6>Launch a Care Plan first</h6>} */}
          </Step.Description>
        </Step.Content>
      </Step>
      <Step disabled={carePlan == null} completed={condition != null}>
        <Step.Content>
          <Step.Title>Screen</Step.Title>
          <Step.Description>
            {carePlan && (
              <Screening
                fhirPatient={props.fhirPatient}
                orgId={props.orgId}
                patientId={props.patientId}
                planDefinition={planDefinition}
                carePlan={carePlan}
                carePlanData={props.carePlanData}
                condition={condition}
                onScreen={handleScreen}
                toggleSurveySidebar={props.toggleSurveySidebar}
                patientName={props.patientName}
                patientEmail={props.patientEmail}
                patientPhone={props.patientPhone}
                // Producer and `Screening` disagree on this type by design — see the
                // SUSPECTED DEFECT on `ScreeningProps['referralData']`.
                referralData={props.referralData as ReferralSummary[] | undefined}
                communicationsMutate={props.communicationsMutate}
                profileMutate={props.profileMutate}
                refreshCareplanTasks={props.refresh}
                questionnaireData={props.questionnaireData}
              />
            )}
          </Step.Description>
        </Step.Content>
      </Step>
      <Step
        disabled={carePlan == null || flatCondition == null || flatCondition.verificationStatusCode !== 'confirmed'}
        completed={intervention ? true : false}
      >
        <Step.Content>
          {props.orgId && (props.orgId === 'IU' || props.orgId === 'ZING') && <Step.Title>Intervention</Step.Title>}
          {props.orgId && (props.orgId !== 'IU') === (props.orgId !== 'ZING') && <Step.Title>Assess</Step.Title>}
          <Step.Description>
            {carePlan && (
              <Assessment
                orgId={props.orgId}
                patientId={props.patientId}
                planDefinition={planDefinition}
                carePlan={carePlan}
                condition={flatCondition}
                tasks={props.tasks}
                onAssess={handleAssess}
                toggleSurveySidebar={props.toggleSurveySidebar}
                communicationsMutate={props.communicationsMutate}
                questionnaireData={props.questionnaireData}
              />
            )}
          </Step.Description>
        </Step.Content>
      </Step>
      {showIntervention && (
        <Step
          disabled={
            carePlan == null ||
            flatCondition == null ||
            flatCondition.verificationStatusCode !== 'confirmed' ||
            (!plannedInterventionId && !intervention)
          }
        >
          <Step.Content>
            <Step.Title>Intervene</Step.Title>
            <Step.Description>
              {carePlan && (
                <Intervention
                  orgId={props.orgId}
                  patientId={props.patientId}
                  planDefinition={planDefinition}
                  carePlan={carePlan}
                  condition={flatCondition}
                  plannedInterventionActionId={plannedInterventionId ?? intervention}
                  onIntervention={handleIntervention}
                  toggleSurveySidebar={props.toggleSurveySidebar}
                  tasks={props.tasks}
                  patientName={props.patientName}
                  patientEmail={props.patientEmail}
                  patientPhone={props.patientPhone}
                  communicationsMutate={props.communicationsMutate}
                  referralData={props.referralData}
                  questionnaireData={props.questionnaireData}
                />
              )}
            </Step.Description>
          </Step.Content>
        </Step>
      )}
    </Step.Group>
  );
}

export default CarePlanSteps;
